import { Button, Empty, Image, Modal, Table, TableProps } from 'antd';
import { useEffect, useState } from 'react';

// services
import NotificationService from 'core/services/notification';
import { onGetCompanies } from 'modules/users/services/users';

// components
import LoadingComponent from 'components/loadingComponent/loadingComponent';

// modals
import { Company } from 'modules/users/models/companies';
import CompanyDetailsComponent from '../company-details/company-details';
import CompanyOverviewComponent from '../company-overview/company-overview';
import CompanyDocumentsComponent from '../company-documents/company-documents';
import { useTranslation } from 'react-i18next';
import NoData from 'components/noData/noData';

interface InputProps {
	id: number;
}

function CompaniesComponent({ id }: InputProps) {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);

	const [detailsOpen, setDetailsOpen] = useState(false);
	const [overviewOpen, setOverviewOpen] = useState(false);
	const [documentsOpen, setDocumentsOpen] = useState(false);

	const [companies, setCompanies] = useState<Company[]>([]);
	const [selectedCompany, setSelectedCompany] = useState<Company | null>();

	useEffect(() => {
		getCompanies();
	}, []);

	const columns: TableProps<Company>['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
			render: (id) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{id}</span>,
		},
		{
			title: t('USERS.COMPANY_NAME'),
			dataIndex: 'company_name',
			key: 'company_name',
			render: (company_name) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{company_name}</span>,
		},
		{
			title: t('USERS.COMPANY_LOGO'),
			dataIndex: 'company_logo',
			key: 'company_logo',
			render: (company_logo) => <Image src={company_logo} height={50} width={50} />,
		},
		{
			title: t('USERS.COMPANY_CODE'),
			dataIndex: 'code',
			key: 'code',
			render: (code) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{code}</span>,
		},
		{
			title: t('USERS.FOUNDER'),
			dataIndex: 'founder',
			key: 'founder',
			render: (data) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{data}</span>,
		},
		{
			title: t('USERS.EXECUTIVE'),
			dataIndex: 'executive',
			key: 'executive',
			render: (data) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{data}</span>,
		},
		{
			title: t('USERS.BOARD_OF_DIRECTORS'),
			dataIndex: 'board_of_directors',
			key: 'board_of_directors',
			render: (data) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{data}</span>,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, company) => (
				<div className="d-flex gap-2">
					<Button type="primary" style={{ padding: '5px 15px' }} onClick={() => onDetailsClick(company)}>
						{t('USERS.DETAILS')}
					</Button>
					<Button type="primary" style={{ padding: '5px 15px' }} onClick={() => onOverviewClick(company)}>
						{t('USERS.OVERVIEW')}
					</Button>
					<Button type="primary" style={{ padding: '5px 15px' }} onClick={() => onDocumentsClick(company)}>
						{t('USERS.DOCUMENTS')}
					</Button>
				</div>
			),
		},
	];

	const getCompanies = async () => {
		try {
			setIsLoading(true);

			const response = await onGetCompanies(id);
			setCompanies(response.data.data.shareholder_companies);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);

			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const onDetailsClick = (company: Company) => {
		setSelectedCompany(company);
		setDetailsOpen(true);
	};

	const onOverviewClick = (company: Company) => {
		setSelectedCompany(company);
		setOverviewOpen(true);
	};

	const onDocumentsClick = (company: Company) => {
		setSelectedCompany(company);
		setDocumentsOpen(true);
	};

	return (
		<>
			{isLoading ? (
				<LoadingComponent />
			) : (
				<Table rowKey="id" columns={columns} dataSource={companies} scroll={{ x: 400 }} locale={{ emptyText: <NoData /> }} />
			)}

			{selectedCompany && (
				<>
					<Modal
						open={detailsOpen}
						onCancel={() => {
							setDetailsOpen(false);
							setSelectedCompany(null);
						}}
						width={1000}>
						<CompanyDetailsComponent company={selectedCompany as Company} />
					</Modal>

					<Modal open={overviewOpen} footer={null} onCancel={() => setOverviewOpen(false)} width={1000}>
						<CompanyOverviewComponent company={selectedCompany as Company} userId={id} />
					</Modal>

					<Modal
						title={t('USERS.COMPANY_DOCUMENTS', { name: selectedCompany?.company_name })}
						open={documentsOpen}
						footer={null}
						styles={{
							body: {
								maxHeight: '70vh',
								overflowY: 'auto',
							},
						}}
						onCancel={() => setDocumentsOpen(false)}>
						<CompanyDocumentsComponent company={selectedCompany as Company} userId={id} />
					</Modal>
				</>
			)}
		</>
	);
}

export default CompaniesComponent;
